import React, { useState, useEffect } from 'react';
import { logger } from '../lib/logger';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format, addDays } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { 
  Car, Clock, Shield, MapPin, CheckCircle, Star, 
  CreditCard, ChevronRight, Phone, Plane, ArrowRight,
  Timer, Users, Lock, Zap, Play, User, LogIn
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { reservationsApi } from '../lib/api';
import { cn } from '../lib/utils';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { BookingBar, ParkingFaq, ParkingNavigation, PageMetadata } from '../components/ParkingContent';
import { useAuth } from '../contexts/AuthContext';
import { fadeInUp, fadeIn, scaleIn, viewportOnce, transitions } from '../lib/animations';

// Time options
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    TIME_OPTIONS.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
  }
}

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const locale = i18n.language === 'es' ? es : enUS;
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState('10:00');
  const [endDate, setEndDate] = useState(null);
  const [endTime, setEndTime] = useState('10:00');
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Calculate price when dates change
  useEffect(() => {
    const calculatePrice = async () => {
      if (!startDate || !endDate) return;
      
      const startDt = new Date(startDate);
      const [startH, startM] = startTime.split(':');
      startDt.setHours(parseInt(startH), parseInt(startM), 0, 0);
      
      const endDt = new Date(endDate);
      const [endH, endM] = endTime.split(':');
      endDt.setHours(parseInt(endH), parseInt(endM), 0, 0);
      
      if (endDt <= startDt) return;

      try {
        const response = await reservationsApi.calculatePrice(startDt.toISOString(), endDt.toISOString());
        setPriceData(response.data);
      } catch (error) {
        logger.error('Error:', error);
      }
    };
    calculatePrice();
  }, [startDate, endDate, startTime, endTime]);

  const handleReserve = () => {
    if (!startDate || !endDate) {
      toast.error('Selecciona las fechas de tu viaje');
      return;
    }
    
    // Store dates in sessionStorage and redirect to booking
    const startDt = new Date(startDate);
    const [startH, startM] = startTime.split(':');
    startDt.setHours(parseInt(startH), parseInt(startM), 0, 0);
    
    const endDt = new Date(endDate);
    const [endH, endM] = endTime.split(':');
    endDt.setHours(parseInt(endH), parseInt(endM), 0, 0);
    
    sessionStorage.setItem('booking_start', startDt.toISOString());
    sessionStorage.setItem('booking_end', endDt.toISOString());
    
    navigate('/reservar?from=landing');
  };

  const scrollToForm = () => {
    document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white parking-with-booking-bar">
      <PageMetadata title="Parking aeropuerto Madrid-Barajas con valet | Pléyades Parking" description="Reserva parking valet en Madrid-Barajas. Recogida y devolución coordinadas en T1, T2 y T4. Consulta el precio total para tus fechas." path="/" />
      {/* Minimal Header - No distractions */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-white/5">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#FFD700] rounded flex items-center justify-center">
              <Car className="w-5 h-5 text-black" />
            </div>
            <span className="font-bold text-lg tracking-tight">PLEYADES</span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher variant="minimal" />
            <a 
              href="tel:641243287" 
              className="flex items-center gap-2 text-sm text-[#FFD700] hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">641 243 287</span>
            </a>
            {isAuthenticated ? (
              <Link 
                to="/account"
                className="flex items-center gap-2 text-sm text-white hover:text-[#FFD700] transition-colors"
                data-testid="my-account-link"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">{t('nav.account')}</span>
              </Link>
            ) : (
              <Link 
                to="/login"
                className="flex items-center gap-2 text-sm text-white hover:text-[#FFD700] transition-colors"
                data-testid="login-link"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">{t('nav.login')}</span>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* ============================================
          HERO SECTION - HIGH CONVERSION VISUAL IMPACT
          ============================================ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video/Image Background */}
        <div className="absolute inset-0">
          {/* Main background image - Valet service at airport */}
          <div 
            className="absolute inset-0 bg-cover bg-center scale-105"
            style={{ 
              backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/c0cb83c7-9d4b-4785-b4e7-ab2b7a0a926c/images/4b1b737cfd2484b7ce837ae7693e510a03508c1323db4b9199de26039555ef7a.png)'
            }}
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/60" />
          {/* Gradient overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
          {/* Gold accent glow */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#FFD700]/10 rounded-full blur-[150px]" />
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 text-center pt-20 pb-12">
          {/* Trust badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-5 py-2.5 rounded-full mb-8 border border-white/10"
          >
            <div className="flex -space-x-1">
              {[...Array(5)].map((_, i) => (
                <Star key={`hero-star-${i}`} className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
              ))}
            </div>
            <span className="text-sm text-white/90 font-medium" data-testid="landing-hero-badge">{t('landingHero.badge')}</span>
          </motion.div>

          {/* HEADLINE - Big and aggressive */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 max-w-5xl mx-auto"
          >
            <span className="text-white">{t('landingHero.headline1')}</span>
            <br />
            <span className="text-[#FFD700]">{t('landingHero.headline2')}</span>
          </motion.h1>

          {/* SUBHEADLINE - Brutal clarity */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg sm:text-xl md:text-2xl text-white/80 mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            {t('landingHero.subheadline1')} <span className="text-white font-semibold">{t('landingHero.subheadline2')}</span>.
            <br className="hidden sm:block" />
            {t('landingHero.subheadline3')}
          </motion.p>

          {/* Quick benefits - 3 columns */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap justify-center gap-6 sm:gap-10 mb-12"
          >
            <div className="flex items-center gap-2 text-white/90">
              <CheckCircle className="w-5 h-5 text-[#FFD700]" />
              <span className="text-sm sm:text-base">{t('landingHero.benefit1')}</span>
            </div>
            <div className="flex items-center gap-2 text-white/90">
              <CheckCircle className="w-5 h-5 text-[#FFD700]" />
              <span className="text-sm sm:text-base">{t('landingHero.benefit2')}</span>
            </div>
            <div className="flex items-center gap-2 text-white/90">
              <CheckCircle className="w-5 h-5 text-[#FFD700]" />
              <span className="text-sm sm:text-base">{t('landingHero.benefit3')}</span>
            </div>
          </motion.div>

          {/* MEGA CTA BUTTON */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, type: 'spring', stiffness: 200 }}
            className="mb-8"
          >
            <Button
              onClick={() => navigate('/reservar')}
              className="bg-[#FFD700] hover:bg-[#E6C200] text-black font-bold text-lg sm:text-xl px-10 sm:px-14 py-7 sm:py-8 rounded-full transition-all hover:scale-105 shadow-2xl shadow-[#FFD700]/30 group"
              data-testid="hero-cta"
            >
              <span>{t('landingHero.cta')}</span>
              <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>

          {/* Microcopy under CTA */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-white/50 text-sm"
          >
            {t('landingHero.microcopy')}
          </motion.p>

          {/* Price preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-12 inline-flex items-center gap-3 bg-white/5 backdrop-blur-sm px-6 py-3 rounded-full border border-white/10"
          >
            <span className="text-white/70 text-sm">{t('landingHero.priceFrom')}</span>
            <span className="text-2xl font-bold text-[#FFD700]">3€</span>
            <span className="text-white/50 text-sm">{t('landingHero.pricePerDay')}</span>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="text-white/40 flex flex-col items-center gap-2 cursor-pointer"
            onClick={() => document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <span className="text-xs uppercase tracking-wider">{t('landingHero.discoverMore')}</span>
            <ChevronRight className="w-5 h-5 rotate-90" />
          </motion.div>
        </div>
      </section>

      {/* ============================================
          TESTIMONIALS - Google Reviews (Right below Hero)
          ============================================ */}
      <section id="testimonials" className="py-12 lg:py-16 bg-gradient-to-b from-black to-[#111]">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-8 lg:mb-12">
            <div className="inline-flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full mb-4">
              <img 
                src="https://www.google.com/favicon.ico" 
                alt="Google" 
                className="w-4 h-4"
              />
              <span className="text-sm text-zinc-400">Reseñas verificadas de Google</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
              Lo que dicen nuestros <span className="text-[#FFD700]">clientes</span>
            </h2>
          </div>

          {/* Reviews Grid - Horizontal scroll on mobile */}
          <div className="max-w-6xl mx-auto">
            <div className="flex lg:grid lg:grid-cols-3 gap-4 lg:gap-6 overflow-x-auto pb-4 lg:pb-0 snap-x snap-mandatory scrollbar-hide -mx-4 px-4 lg:mx-0 lg:px-0">
              {[
                { 
                  name: 'Carlos M.',
                  title: 'La mejor decisión antes de viajar',
                  text: 'Llegamos directamente a la terminal, entregamos el coche en menos de dos minutos y nos fuimos al embarque sin estrés. A la vuelta ya estaban esperándonos. Servicio profesional y muy puntual.',
                  tag: 'Comodidad',
                  tagColor: 'bg-blue-500/20 text-blue-400'
                },
                { 
                  name: 'Laura G.',
                  title: 'Rápido, cómodo y totalmente recomendable',
                  text: 'Nada de buscar parking ni caminar con maletas. El conductor nos recibió en la puerta y todo fue ágil y organizado. Repetiremos sin duda.',
                  tag: 'Rapidez',
                  tagColor: 'bg-green-500/20 text-green-400'
                },
                { 
                  name: 'Javier R.',
                  title: 'Servicio serio y seguro',
                  text: 'Al principio tenía dudas, pero el trato fue excelente. Instalaciones vigiladas y comunicación clara en todo momento. Se nota que trabajan con profesionalidad.',
                  tag: 'Confianza',
                  tagColor: 'bg-purple-500/20 text-purple-400'
                },
                { 
                  name: 'Marta L.',
                  title: 'Viajar así marca la diferencia',
                  text: 'La comodidad de entregar el coche en la terminal no tiene precio. Todo coordinado por WhatsApp y puntualidad perfecta. Experiencia de 10.',
                  tag: 'Premium',
                  tagColor: 'bg-[#FFD700]/20 text-[#FFD700]'
                },
                { 
                  name: 'Andrés P.',
                  title: 'Viaje sin preocupaciones',
                  text: 'Reservé online en menos de un minuto y todo fue exactamente como explican en la web. A la vuelta, el coche estaba listo. Servicio impecable.',
                  tag: 'Tranquilidad',
                  tagColor: 'bg-teal-500/20 text-teal-400'
                },
              ].map((review, i) => (
                <motion.div
                  key={`review-${review.title.substring(0,10)}-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex-shrink-0 w-[300px] sm:w-[340px] lg:w-auto bg-white/5 border border-white/10 rounded-xl p-5 lg:p-6 snap-start hover:border-[#FFD700]/30 transition-all"
                  data-testid={`review-card-${i}`}
                >
                  {/* Rating + Tag */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, j) => (
                        <Star key={`review-star-${i}-${j}`} className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
                      ))}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${review.tagColor}`}>
                      {review.tag}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="font-bold text-white mb-2 text-sm lg:text-base">
                    "{review.title}"
                  </h3>

                  {/* Text */}
                  <p className="text-zinc-400 text-sm leading-relaxed mb-4 line-clamp-4">
                    {review.text}
                  </p>

                  {/* Author */}
                  <div className="flex items-center justify-between pt-3 border-t border-white/10">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#FFD700]/20 rounded-full flex items-center justify-center">
                        <span className="text-[#FFD700] font-bold text-sm">
                          {review.name.charAt(0)}
                        </span>
                      </div>
                      <span className="text-sm font-medium">{review.name}</span>
                    </div>
                    <span className="text-xs text-zinc-500">Google Review</span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Scroll hint for mobile */}
            <div className="flex lg:hidden justify-center mt-4">
              <div className="flex items-center gap-2 text-zinc-500 text-xs">
                <ChevronRight className="w-4 h-4" />
                <span>Desliza para ver más</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          BENEFITS - Quick and clear
          ============================================ */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ahorra tiempo. <span className="text-[#FFD700]">Viaja sin estrés.</span>
            </h2>
          </div>

          <div className="max-w-5xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Timer, text: 'No busques parking', desc: 'Olvídate de dar vueltas buscando hueco' },
              { icon: MapPin, text: 'Entrega en terminal', desc: 'Te recogemos en la puerta del aeropuerto' },
              { icon: Shield, text: 'Vehículo protegido 24/7', desc: 'Parking vigilado y asegurado' },
              { icon: Zap, text: 'Reserva en 60 segundos', desc: 'Proceso rápido y sin complicaciones' },
              { icon: CreditCard, text: 'Pago flexible', desc: 'Online o al entregar el vehículo' },
              { icon: CheckCircle, text: 'Cancelación gratuita', desc: 'Hasta 24h antes sin coste' },
            ].map((item, i) => (
              <motion.div
                key={`benefit-${item.text.substring(0,10)}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#FFD700]/30 transition-colors"
              >
                <item.icon className="w-10 h-10 text-[#FFD700] mb-4" />
                <h3 className="font-bold text-lg mb-2">{item.text}</h3>
                <p className="text-zinc-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          HOW IT WORKS - Super simple
          ============================================ */}
      <section className="py-20 bg-gradient-to-b from-[#0A0A0A] to-[#111]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              ¿Cómo funciona?
            </h2>
            <p className="text-zinc-400">Tan simple como 1, 2, 3</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { 
                  step: '1', 
                  title: 'Reserva online', 
                  desc: 'En menos de 60 segundos',
                  icon: CreditCard
                },
                { 
                  step: '2', 
                  title: 'Entrega tu vehículo', 
                  desc: 'En la puerta de la terminal',
                  icon: Car
                },
                { 
                  step: '3', 
                  title: 'Te lo devolvemos', 
                  desc: 'Cuando aterrices, te esperamos',
                  icon: Plane
                },
              ].map((item, i) => (
                <motion.div
                  key={`step-${item.step}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="relative text-center"
                >
                  <div className="w-16 h-16 bg-[#FFD700] rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-black">{item.step}</span>
                  </div>
                  {i < 2 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#FFD700] to-transparent" />
                  )}
                  <h3 className="font-bold text-xl mb-2">{item.title}</h3>
                  <p className="text-zinc-400">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SOCIAL PROOF
          ============================================ */}
      <section className="py-20 bg-[#111]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
              {[
                { id: 'clients', value: '+100.000', label: 'Clientes' },
                { id: 'rating', value: '4.9/5', label: 'Valoración' },
                { id: 'since', value: 'Desde 2021', label: 'Operando' },
                { id: 'insured', value: '100%', label: 'Asegurado' },
              ].map((stat, i) => (
                <motion.div
                  key={`stat-${stat.label}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#FFD700]" data-testid={`landing-stat-${stat.id}-value`}>{stat.value}</p>
                  <p className="text-zinc-400 text-sm" data-testid={`landing-stat-${stat.id}-label`}>{stat.label}</p>
                </motion.div>
              ))}
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center items-center gap-8 mt-12 pt-12 border-t border-white/10">
              <div className="flex items-center gap-2 text-zinc-400">
                <Lock className="w-5 h-5" />
                <span className="text-sm">Pago seguro</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <Shield className="w-5 h-5" />
                <span className="text-sm">Seguro incluido</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <CreditCard className="w-5 h-5" />
                <span className="text-sm">Visa / Mastercard</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          URGENCY + FINAL CTA
          ============================================ */}
      <section className="py-20 bg-gradient-to-b from-[#111] to-black relative overflow-hidden">
        {/* Glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FFD700]/10 rounded-full blur-[150px]" />
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            {/* Urgency message */}
            <div className="inline-flex items-center gap-2 bg-red-500/10 text-red-400 px-4 py-2 rounded-full mb-8">
              <Timer className="w-4 h-4" />
              <span className="text-sm font-medium">Plazas de recogida limitadas en horas punta</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Reserva tu <span className="text-[#FFD700]">valet parking</span> ahora
              <br />y evita complicaciones
            </h2>

            <p className="text-xl text-zinc-400 mb-10">
              No pierdas tiempo buscando parking. Llega, entrega y vuela.
            </p>

            <Button
              onClick={() => navigate('/reservar')}
              className="bg-[#FFD700] hover:bg-[#E6C200] text-black font-bold text-xl px-12 py-7 transition-all hover:scale-105 shadow-lg shadow-[#FFD700]/20"
              data-testid="final-cta"
            >
              Reservar mi valet parking
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>

            <p className="text-zinc-500 text-sm mt-6">
              Sin compromiso · Puedes cancelar gratis hasta 24h antes
            </p>
          </div>
        </div>
      </section>

      {/* ============================================
          MINIMAL FOOTER
          ============================================ */}
      <ParkingFaq />
      <ParkingNavigation />
      <BookingBar />
      <footer className="py-8 bg-black border-t border-white/5">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#FFD700] rounded flex items-center justify-center">
                <Car className="w-4 h-4 text-black" />
              </div>
              <span className="font-bold">PLEYADES PARKING</span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <a href="/terminos" className="text-zinc-400 hover:text-primary transition-colors">
                Términos y Condiciones
              </a>
              <span className="text-zinc-700">|</span>
              <a href="/privacidad" className="text-zinc-400 hover:text-primary transition-colors">
                Privacidad
              </a>
              <span className="text-zinc-700">|</span>
              <a href="mailto:julioviv@pleyadesparking.es" className="text-zinc-400 hover:text-primary transition-colors">
                Contacto
              </a>
            </div>
          </div>
          <div className="mt-4 text-center">
            <p className="text-zinc-500 text-sm">
              © 2026 Pleyades Parking · Valet Parking Aeropuerto Madrid
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
